import type React from "react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { List, IconButton, Collapse, Box, useTheme } from "@mui/material";
import { KeyboardArrowDown as ArrowDownIcon, Settings as SettingsIcon } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import SidebarMenuItem from "../SidebarMenuItem";
import { rawColor } from "../../constants/colors";
import { TestId } from "../../constants/testIds";
import {
  LocationCity as CitiesIcon,
  Store as VendorsIcon,
  People as CustomersIcon,
} from "@mui/icons-material";

type TranslationKeys = keyof typeof translations.en;

interface StaticValuesMenuProps {
  onNavigate?: () => void;
}

const StaticValuesMenu: React.FC<StaticValuesMenuProps> = ({ onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const manuallyCollapsedRef = useRef(false);
  const manuallyExpandedRef = useRef(false);

  // Check if current path is a static values page
  const isStaticValuesPage = useMemo(() => {
    return (
      location.pathname === "/locations" ||
      location.pathname === "/vendors" ||
      location.pathname === "/customers"
    );
  }, [location.pathname]);

  // Initialize expanded state: collapsed by default, expanded if on static values page
  const [staticValuesExpanded, setStaticValuesExpanded] = useState(() => isStaticValuesPage);

  // Auto-expand/collapse based on static values page status and manual state
  useEffect(() => {
    if (isStaticValuesPage) {
      // On static values page: respect manual state
      if (manuallyExpandedRef.current) {
        // Keep expanded if manually expanded
        setStaticValuesExpanded(true);
      } else if (manuallyCollapsedRef.current) {
        // Keep collapsed if manually collapsed
        setStaticValuesExpanded(false);
      } else {
        // Auto-expand on static values pages if no manual state
        setStaticValuesExpanded(true);
      }
    } else {
      // Not on static values page: always auto-collapse and reset manual state
      setStaticValuesExpanded(false);
      manuallyExpandedRef.current = false;
      manuallyCollapsedRef.current = false;
    }
  }, [isStaticValuesPage]);

  const toggleStaticValues = useCallback(() => {
    setStaticValuesExpanded((prev) => {
      const newValue = !prev;
      // Track manual state
      if (newValue) {
        // Manually expanded
        manuallyExpandedRef.current = true;
        manuallyCollapsedRef.current = false;
      } else {
        // Manually collapsed
        manuallyCollapsedRef.current = true;
        manuallyExpandedRef.current = false;
      }
      return newValue;
    });
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        <SidebarMenuItem
          to="/locations"
          selected={
            location.pathname === "/locations" ||
            location.pathname === "/vendors" ||
            location.pathname === "/customers"
          }
          icon={<SettingsIcon />}
          text={t("Static Values" as TranslationKeys)}
          sx={{ flex: 1 }}
          onNavigate={onNavigate}
          data-testid={TestId.SIDEBAR.MENU_ITEM_STATIC_VALUES}
        />
        <IconButton
          size="small"
          onClick={toggleStaticValues}
          sx={{
            transform: staticValuesExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shorter,
            }),
            position: "absolute",
            right: 4,
            zIndex: 1,
            color: rawColor.white.transparent["07"],
            "&:hover": {
              backgroundColor: rawColor.white.transparent["12"],
            },
          }}
        >
          <ArrowDownIcon />
        </IconButton>
      </Box>
      <Collapse in={staticValuesExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding data-testid={TestId.SIDEBAR.STATIC_VALUES_MENU}>
          <SidebarMenuItem
            to="/locations"
            selected={location.pathname === "/locations"}
            icon={<CitiesIcon fontSize="small" />}
            text={t("Locations" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_LOCATIONS}
          />
          <SidebarMenuItem
            to="/vendors"
            selected={location.pathname === "/vendors"}
            icon={<VendorsIcon fontSize="small" />}
            text={t("Vendors" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_VENDORS}
          />
          <SidebarMenuItem
            to="/customers"
            selected={location.pathname === "/customers"}
            icon={<CustomersIcon fontSize="small" />}
            text={t("Customers" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_CUSTOMERS}
          />
        </List>
      </Collapse>
    </Box>
  );
};

export default StaticValuesMenu;
